/**
 * Project-level gating taxonomy.
 *
 * Determines which axis the viewport layout flips along:
 * - "none" — single series, no axis matters; falls through to the default
 *   non-segmentation layout.
 * - "view_dependent" — anatomy plane is the discriminator (axial / coronal /
 *   sagittal). Used by stones and other multi-plane segmentation tasks.
 * - "phase_dependent" — contrast phase is the discriminator (arterial / portal
 *   venous / delayed / non-contrast). Used by hepatic-lesion segmentation.
 *
 * The mode is set on the worklist payload by the backend; the frontend should
 * fall back to "none" if the field is absent.
 */
export const GATING_MODE = {
  NONE: "none",
  VIEW_DEPENDENT: "view_dependent",
  PHASE_DEPENDENT: "phase_dependent",
  SLICE_DEPENDENT: "slice_dependent",
} as const;
export type GatingMode = (typeof GATING_MODE)[keyof typeof GATING_MODE];

/**
 * Contrast phases used by phase_dependent layouts.
 *
 * Note: `delayed` and `equilibrium` are clinically distinct timing points
 * (delayed ~3 min post contrast, equilibrium ~5+ min) that some protocols
 * acquire as separate series (e.g. Italian liver protocols label them
 * `TARDIA` and `EQUILIBRIO`). The cohort builder currently does not split
 * these — that's a frontend-only distinction for now.
 */
export type Phase = "arterial" | "portal_venous" | "delayed" | "equilibrium" | "noncontrast";

/** Anatomic plane used by view_dependent layouts. */
export type ViewAxis = "axial" | "coronal" | "sagittal";

/** A single discriminator-to-series mapping for the active study. */
export type Discriminator =
  | { kind: "phase"; value: Phase; seriesInstanceUid: string }
  | { kind: "view"; value: ViewAxis; seriesInstanceUid: string };

/**
 * Chronological order for phase cycling. Used by Tab cycling and flicker.
 * Matches the typical contrast bolus timeline: pre → arterial → PV → delayed.
 */
export const PHASE_ORDER: readonly Phase[] = [
  "noncontrast",
  "arterial",
  "portal_venous",
  "delayed",
  "equilibrium",
] as const;

/** Display label for a phase. */
export const PHASE_LABEL: Record<Phase, string> = {
  noncontrast: "Non-Contrast",
  arterial: "Arterial",
  portal_venous: "Portal Venous",
  delayed: "Delayed",
  equilibrium: "Equilibrium",
};

/** Short display label (for tabs). */
export const PHASE_LABEL_SHORT: Record<Phase, string> = {
  noncontrast: "NC",
  arterial: "Art",
  portal_venous: "PV",
  delayed: "Del",
  equilibrium: "Eq",
};

/**
 * Preferred phase for segmentation when multiple are available.
 * PV is the radiology default for liver lesion segmentation.
 */
export const DEFAULT_SEGMENTATION_PHASE: Phase = "portal_venous";
